import { Component, OnInit, Input, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FikoRaycastConfigService, RAYCAST_PROFILES, RaycastConfig } from '../services/fiko-raycast-config.service';
import { FikoThreeJsComponent } from '../fiko-three-js.component';

@Component({
  selector: 'fiko-threejs-ayarlar',
  templateUrl: './fiko-threejs-ayarlar.component.html',
  styleUrls: ['./fiko-threejs-ayarlar.component.scss'],
  imports: [CommonModule, FormsModule],
  standalone: true
})
export class FikoThreeJsAyarlarComponent implements OnInit {
  @Input() pencere?: any; // Dialog referansı
  @Input() ayarlarDegistiCallback?: (ayarlar: any) => void; // Callback fonksiyonu
  @Output() ayarlarDegisti = new EventEmitter<any>();

  // ===== RENDER AYARLARI (MEVCUT) =====
  renderModu: 'on-demand' | 'continuous' = 'on-demand';
  surekliCizimModu: boolean = true; // 🚀 Sürekli çizim modu (default: aktif)
  maxFPS: number = 60;
  antialiasAktif: boolean = false;

  // ===== OPTİMİZASYON AYARLARI (MEVCUT) =====
  frustumCullingAktif: boolean = true;
  lodSistemiAktif: boolean = true;

  // ===== KAMERA AYARLARI (MEVCUT) =====
  pixelRatio: number = 1.0;
  pixelRatioMin: number = 0.5;
  pixelRatioMax: number = 2.0;

  // ===== GPU OPTIMIZATION AYARLARI =====
  dynamicResolutionAktif: boolean = false;
  gpuParticleComputeAktif: boolean = false;
  temporalAAaktif: boolean = false;
  virtualTexturesAktif: boolean = false;  // textureStreamingAktif → virtualTexturesAktif (consistency)
  shadowOptimizerAktif: boolean = false;

  // ===== GPU COMPUTE EXTREME AYARLARI =====
  gpuPhysicsAktif: boolean = false;
  gpuFrustumCullingAktif: boolean = false;
  gpuOcclusionQueriesAktif: boolean = false;
  gpuTransformFeedbackAktif: boolean = false;
  gpuSkinningAktif: boolean = false;
  gpuParticleAdvectionAktif: boolean = false;
  gpuTextureFilteringAktif: boolean = false;
  gpuTileDeferredRenderingAktif: boolean = false;
  gpuAsyncComputeAktif: boolean = false;
  gpuStreamOutputAktif: boolean = false;

  // ===== ADVANCED OPTIMIZATIONS =====
  aggressiveLODAktif: boolean = false;
  computeWorkerPoolAktif: boolean = false;
  zPrepassAktif: boolean = false;
  frameBudgetAktif: boolean = false;
  instancedOptimizerAktif: boolean = false;
  gpuDrivenRendererAktif: boolean = false;
  visibilityPrecomputeAktif: boolean = false;
  aggressiveMergingAktif: boolean = false;
  // virtualTexturesAktif ve neuralCompressionAktif GPU Optimization bölümünde tanımlanmıştır
  neuralCompressionAktif: boolean = false;

  // ===== SEÇİM VE HIGHLIGHT AYARLARI =====
  // Yüzey ayarları
  hoverRengi: string = '#00ffff';
  hoverOpacity: number = 0.5;
  secilmisRengi: string = '#ff0000';
  secilmisOpacity: number = 0.5;

  // Nokta Koy ayarları
  noktaKoyBoyutu: number = 20;           // Nokta koy hover/kalıcı nokta boyutu (piksel)
  noktaKoyHoverRengi: string = '#FFD700'; // Hover noktası rengi (altın)

  // Material ayarları (ışıklandırma/gölge)
  highlightSaydamlik: boolean = false;      // true: şeffaf, false: opak
  highlightRoughness: number = 0.3;         // Pürüzlülük (düşük = parlak)
  highlightMetalness: number = 0.2;         // Metaliklik
  highlightEmissiveIntensity: number = 0.8; // Işıma yoğunluğu (yüksek = parlak)

  // Çizgi ayarları
  cizgiHoverRengi: string = '#00ff00';      // Green - Çizgi hover rengi
  cizgiHoverOpacity: number = 0.7;
  cizgiSecilmisRengi: string = '#ffff00';   // Yellow - Çizgi seçili rengi
  cizgiSecilmisOpacity: number = 0.7;
  cizgiKalinligi: number = 5;               // Çizgi kalınlığı (linewidth)

  // Kenar ayarları
  kenarGucluklagi: number = 10;
  kenarYogunluklagi: number = 1.5;
  kenarKalinligi: number = 1.0;
  gorunurKenarRengi: string = '#ffffff';
  gizliKenarRengi: string = '#000000';

  // ===== KENAR/NOKTA SEÇİM AYARLARI (2D Piksel Tabanlı) =====
  pikselTabanliSecimAktif: boolean = true;   // 2D piksel tabanlı seçim sistemi aktif/pasif (master switch)
  noktaSecimAktif: boolean = true;           // Nokta seçimi aktif/pasif
  kenarSecimAktif: boolean = true;           // Kenar (edge) seçimi aktif/pasif
  yuzeySecimAktif: boolean = true;           // Yüzey (mesh) seçimi aktif/pasif
  kenarSecimPikselToleransi: number = 30;    // Kenar seçim toleransı (piksel)
  noktaSecimPikselToleransi: number = 25;    // Nokta seçim toleransı (piksel)
  akilliOncelikAktif: boolean = true;        // Akıllı öncelik sistemi (yüzey varken daha sıkı tolerans)
  yuzeyOncelikEsigi: number = 15;            // Yüzey varken kenar/nokta için piksel eşiği
  cizgiOncelikliSecim: boolean = true;       // Çizgi/kenar seçimine öncelik ver (yüzey üzerinde bile)

  // ===== PERFORMANS AYARLARI (Büyük Dosyalar İçin) =====
  hoverThrottleMs: number = 16;              // Hover throttle (ms) - yüksek = daha az CPU (16=60fps, 33=30fps, 50=20fps)
  maxEdgeObjesiSayisi: number = 500;         // Maksimum taranacak edge objesi sayısı
  // frustumCullingAktif zaten yukarıda "OPTİMİZASYON AYARLARI" bölümünde tanımlı
  uzaklikFiltreAktif: boolean = true;        // Çok uzak objeleri atla
  uzaklikFiltreEsigi: number = 1000;         // Bu mesafeden uzak objeleri atla
  erkenCikisAktif: boolean = true;           // Yeterince yakın kenar bulunca dur
  erkenCikisMesafesi: number = 5;            // Bu piksel mesafesinden yakın bulunca dur

  // ===== HOVER HIGHLIGHT TİP AYARLARI =====
  hoverHighlightMesh: boolean = true;        // Mesh (yüzey) hover highlight aktif
  hoverHighlightLine: boolean = true;        // Line (çizgi) hover highlight aktif
  hoverHighlightPoint: boolean = true;       // Point (nokta) hover highlight aktif
  hoverHighlightEdge: boolean = true;        // Edge (kenar) hover highlight aktif

  // ===== SEÇİM HIGHLIGHT TİP AYARLARI =====
  selectionHighlightMesh: boolean = true;    // Mesh (yüzey) seçim highlight aktif
  selectionHighlightLine: boolean = true;    // Line (çizgi) seçim highlight aktif
  selectionHighlightPoint: boolean = true;   // Point (nokta) seçim highlight aktif
  selectionHighlightEdge: boolean = true;    // Edge (kenar) seçim highlight aktif

  // ===== DEBUG AYARLARI =====
  debugMode: boolean = false;

  // ===== ÖLÇÜM AYARLARI =====
  olcumCizgiRengi: string = '#000000';           // 🎯 SİYAH - Ana ölçüm çizgi/ok rengi
  olcumCizgiKalinligi: number = 1.5;             // Çizgi kalınlığı (CATIA tarzı)
  olcumOffsetMesafe: number = 10;                // Offset mesafesi
  olcumYaziFontSize: number = 12;                // Yazı boyutu
  olcumYaziRengi: string = '#000000';            // Yazı rengi (siyah - profesyonel)
  olcumYaziBackgroundAlpha: number = 0;          // Yazı arka plan şeffaflığı (0 = transparan)

  // ===== RAYCAST AYARLARI =====
  raycastProfili: string = 'DEFAULT';
  raycastProfilListi: string[] = [];
  mevcut_raycast_config: RaycastConfig = { ...RAYCAST_PROFILES.DEFAULT };

  // ===== DURUM DEĞİŞKENLERİ =====
  sekmeler = {
    render: true,
    optimizasyon: false,
    kamera: false,
    gpuOptimization: false,
    gpuCompute: false,
    advancedOptimization: false,
    secimVeHighlight: false,
    olcum: false,
    raycast: false,
    debug: false
  };

  constructor(
    private cd: ChangeDetectorRef,
    private raycastConfigService: FikoRaycastConfigService
  ) {}

  ngOnInit() {
    this.loadAyarlar();
    this.yukleProfilleri();
    this.raycastAyarlariniOkuAbonelik();
  }

  /**
   * Raycast profil listesini yükle
   */
  private yukleProfilleri(): void {
    this.raycastProfilListi = Object.keys(RAYCAST_PROFILES);
  }

  /**
   * Raycast ayarlarına abone ol
   */
  private raycastAyarlariniOkuAbonelik(): void {
    this.raycastConfigService.config$.subscribe(config => {
      this.mevcut_raycast_config = { ...config };
      this.cd.detectChanges();
    });

    this.raycastConfigService.currentProfile$.subscribe(profile => {
      this.raycastProfili = profile;
      this.cd.detectChanges();
    });
  }

  /**
   * Raycast profili değiştir
   */
  raycastProfiliniDegistir(profilAdi: string): void {
    if (profilAdi in RAYCAST_PROFILES) {
      this.raycastConfigService.applyProfile(profilAdi as keyof typeof RAYCAST_PROFILES);
    }
  }

  /**
   * Threshold değerini güncelle
   */
  raycastThresholdGuncelle(type: keyof RaycastConfig, value: number): void {
    this.raycastConfigService.updateThreshold(type, value);
  }

  /**
   * Kaydedilen ayarları yükle
   */
  loadAyarlar() {
    const kaydedilen = localStorage.getItem('FikoThreeJSAyarlari');
    if (kaydedilen) {
      try {
        const parsed = JSON.parse(kaydedilen);
        // Eski property ismini uyumlu hale getir (textureStreamingAktif → virtualTexturesAktif)
        if ('textureStreamingAktif' in parsed) {
          parsed.virtualTexturesAktif = parsed.textureStreamingAktif;
          delete parsed.textureStreamingAktif;
        }
        Object.assign(this, parsed);
        this.cd.detectChanges();
      } catch (e) {
        // Error loading settings - silently ignored
      }
    }
  }

  /**
   * Ayarları uygula (Three.js component'e gönder)
   */
  ayarlariUygula(ayarlar: any) {
    // Dialog açıldığında veya ayarlar değiştiğinde çağrılır
    FikoThreeJsComponent.debugLog('Three.js ayarları uygulandı:', ayarlar);
  }

  /**
   * Ayarları kaydet
   */
  kaydetAyarlar() {
    const ayarlarObj = {
      renderModu: this.renderModu,
      surekliCizimModu: this.surekliCizimModu,
      maxFPS: this.maxFPS,
      antialiasAktif: this.antialiasAktif,
      frustumCullingAktif: this.frustumCullingAktif,
      lodSistemiAktif: this.lodSistemiAktif,
      pixelRatio: this.pixelRatio,
      // GPU Optimization
      dynamicResolutionAktif: this.dynamicResolutionAktif,
      gpuParticleComputeAktif: this.gpuParticleComputeAktif,
      temporalAAaktif: this.temporalAAaktif,
      virtualTexturesAktif: this.virtualTexturesAktif,
      shadowOptimizerAktif: this.shadowOptimizerAktif,
      // GPU Compute Extreme
      gpuPhysicsAktif: this.gpuPhysicsAktif,
      gpuFrustumCullingAktif: this.gpuFrustumCullingAktif,
      gpuOcclusionQueriesAktif: this.gpuOcclusionQueriesAktif,
      gpuTransformFeedbackAktif: this.gpuTransformFeedbackAktif,
      gpuSkinningAktif: this.gpuSkinningAktif,
      gpuParticleAdvectionAktif: this.gpuParticleAdvectionAktif,
      gpuTextureFilteringAktif: this.gpuTextureFilteringAktif,
      gpuTileDeferredRenderingAktif: this.gpuTileDeferredRenderingAktif,
      gpuAsyncComputeAktif: this.gpuAsyncComputeAktif,
      gpuStreamOutputAktif: this.gpuStreamOutputAktif,
      // Advanced
      aggressiveLODAktif: this.aggressiveLODAktif,
      computeWorkerPoolAktif: this.computeWorkerPoolAktif,
      zPrepassAktif: this.zPrepassAktif,
      frameBudgetAktif: this.frameBudgetAktif,
      instancedOptimizerAktif: this.instancedOptimizerAktif,
      gpuDrivenRendererAktif: this.gpuDrivenRendererAktif,
      visibilityPrecomputeAktif: this.visibilityPrecomputeAktif,
      aggressiveMergingAktif: this.aggressiveMergingAktif,
      // virtualTexturesAktif ve neuralCompressionAktif GPU Optimization bölümünde kaydediliyor
      neuralCompressionAktif: this.neuralCompressionAktif,
      // Seçim ve Highlight - Yüzey
      hoverRengi: this.hoverRengi,
      hoverOpacity: this.hoverOpacity,
      secilmisRengi: this.secilmisRengi,
      secilmisOpacity: this.secilmisOpacity,
      // Nokta Koy ayarları
      noktaKoyBoyutu: this.noktaKoyBoyutu,
      noktaKoyHoverRengi: this.noktaKoyHoverRengi,
      // Material ayarları
      highlightSaydamlik: this.highlightSaydamlik,
      highlightRoughness: this.highlightRoughness,
      highlightMetalness: this.highlightMetalness,
      highlightEmissiveIntensity: this.highlightEmissiveIntensity,
      // Çizgi
      cizgiHoverRengi: this.cizgiHoverRengi,
      cizgiHoverOpacity: this.cizgiHoverOpacity,
      cizgiSecilmisRengi: this.cizgiSecilmisRengi,
      cizgiSecilmisOpacity: this.cizgiSecilmisOpacity,
      cizgiKalinligi: this.cizgiKalinligi,
      // Kenarlar
      kenarGucluklagi: this.kenarGucluklagi,
      kenarYogunluklagi: this.kenarYogunluklagi,
      kenarKalinligi: this.kenarKalinligi,
      gorunurKenarRengi: this.gorunurKenarRengi,
      gizliKenarRengi: this.gizliKenarRengi,
      // Kenar/Nokta seçim ayarları (2D piksel tabanlı)
      pikselTabanliSecimAktif: this.pikselTabanliSecimAktif,
      noktaSecimAktif: this.noktaSecimAktif,
      kenarSecimAktif: this.kenarSecimAktif,
      yuzeySecimAktif: this.yuzeySecimAktif,
      kenarSecimPikselToleransi: this.kenarSecimPikselToleransi,
      noktaSecimPikselToleransi: this.noktaSecimPikselToleransi,
      akilliOncelikAktif: this.akilliOncelikAktif,
      yuzeyOncelikEsigi: this.yuzeyOncelikEsigi,
      cizgiOncelikliSecim: this.cizgiOncelikliSecim,
      // Performans ayarları
      hoverThrottleMs: this.hoverThrottleMs,
      maxEdgeObjesiSayisi: this.maxEdgeObjesiSayisi,
      // frustumCullingAktif zaten yukarıda tanımlı (satır 238)
      uzaklikFiltreAktif: this.uzaklikFiltreAktif,
      uzaklikFiltreEsigi: this.uzaklikFiltreEsigi,
      erkenCikisAktif: this.erkenCikisAktif,
      erkenCikisMesafesi: this.erkenCikisMesafesi,
      // Hover highlight tip ayarları
      hoverHighlightMesh: this.hoverHighlightMesh,
      hoverHighlightLine: this.hoverHighlightLine,
      hoverHighlightPoint: this.hoverHighlightPoint,
      hoverHighlightEdge: this.hoverHighlightEdge,
      // Seçim highlight tip ayarları
      selectionHighlightMesh: this.selectionHighlightMesh,
      selectionHighlightLine: this.selectionHighlightLine,
      selectionHighlightPoint: this.selectionHighlightPoint,
      selectionHighlightEdge: this.selectionHighlightEdge,
      // Ölçüm ayarları
      olcumCizgiRengi: this.olcumCizgiRengi,
      olcumCizgiKalinligi: this.olcumCizgiKalinligi,
      olcumOffsetMesafe: this.olcumOffsetMesafe,
      olcumYaziFontSize: this.olcumYaziFontSize,
      olcumYaziRengi: this.olcumYaziRengi,
      olcumYaziBackgroundAlpha: this.olcumYaziBackgroundAlpha
    };

    localStorage.setItem('FikoThreeJSAyarlari', JSON.stringify(ayarlarObj));
    this.ayarlarDegisti.emit(ayarlarObj);

    // Callback'i çağır (Three.js component'e haber ver)
    if (this.ayarlarDegistiCallback) {
      this.ayarlarDegistiCallback(ayarlarObj);
    }
  }

  /**
   * Varsayılan ayarları geri yükle
   */
  resetAyarlar() {
    this.renderModu = 'on-demand';
    this.surekliCizimModu = true; // Default: aktif
    this.maxFPS = 60;
    this.antialiasAktif = false;
    this.frustumCullingAktif = true;
    this.lodSistemiAktif = true;
    this.pixelRatio = 1.0;

    // Varsayılan: GPU optimizasyonları kapalı
    this.dynamicResolutionAktif = false;
    this.gpuParticleComputeAktif = false;
    this.temporalAAaktif = false;
    this.virtualTexturesAktif = false;
    this.shadowOptimizerAktif = false;
    this.gpuPhysicsAktif = false;
    this.gpuFrustumCullingAktif = false;
    this.gpuOcclusionQueriesAktif = false;
    this.gpuTransformFeedbackAktif = false;
    this.gpuSkinningAktif = false;
    this.gpuParticleAdvectionAktif = false;
    this.gpuTextureFilteringAktif = false;
    this.gpuTileDeferredRenderingAktif = false;
    this.gpuAsyncComputeAktif = false;
    this.gpuStreamOutputAktif = false;
    this.aggressiveLODAktif = false;
    this.computeWorkerPoolAktif = false;
    this.zPrepassAktif = false;
    this.frameBudgetAktif = false;
    this.instancedOptimizerAktif = false;
    this.gpuDrivenRendererAktif = false;
    this.visibilityPrecomputeAktif = false;
    this.aggressiveMergingAktif = false;
    this.virtualTexturesAktif = false;
    this.neuralCompressionAktif = false;

    // Seçim ve Highlight varsayılanları - Yüzey
    this.hoverRengi = '#00ffff';
    this.hoverOpacity = 0.5;
    this.secilmisRengi = '#ff0000';
    this.secilmisOpacity = 0.5;
    // Nokta Koy varsayılanları
    this.noktaKoyBoyutu = 20;
    this.noktaKoyHoverRengi = '#FFD700';
    // Material varsayılanları
    this.highlightSaydamlik = false;
    this.highlightRoughness = 0.3;
    this.highlightMetalness = 0.2;
    this.highlightEmissiveIntensity = 0.8;
    // Çizgi varsayılanları
    this.cizgiHoverRengi = '#00ff00';
    this.cizgiHoverOpacity = 0.7;
    this.cizgiSecilmisRengi = '#ffff00';
    this.cizgiSecilmisOpacity = 0.7;
    this.cizgiKalinligi = 5;
    // Kenar varsayılanları
    this.kenarGucluklagi = 10;
    this.kenarYogunluklagi = 1.5;
    this.kenarKalinligi = 1.0;
    this.gorunurKenarRengi = '#ffffff';
    this.gizliKenarRengi = '#000000';

    // Kenar/Nokta seçim varsayılanları (2D piksel tabanlı)
    this.pikselTabanliSecimAktif = true;
    this.noktaSecimAktif = true;
    this.kenarSecimAktif = true;
    this.yuzeySecimAktif = true;
    this.kenarSecimPikselToleransi = 30;
    this.noktaSecimPikselToleransi = 25;
    this.akilliOncelikAktif = true;
    this.yuzeyOncelikEsigi = 15;
    this.cizgiOncelikliSecim = true;
    // Performans varsayılanları
    this.hoverThrottleMs = 16;
    this.maxEdgeObjesiSayisi = 500;
    this.frustumCullingAktif = true;
    this.uzaklikFiltreAktif = true;
    this.uzaklikFiltreEsigi = 1000;
    this.erkenCikisAktif = true;
    this.erkenCikisMesafesi = 5;

    // Hover highlight tip varsayılanları
    this.hoverHighlightMesh = true;
    this.hoverHighlightLine = true;
    this.hoverHighlightPoint = true;
    this.hoverHighlightEdge = true;

    // Seçim highlight tip varsayılanları
    this.selectionHighlightMesh = true;
    this.selectionHighlightLine = true;
    this.selectionHighlightPoint = true;
    this.selectionHighlightEdge = true;

    // Ölçüm varsayılanları (CATIA tarzı profesyonel - SİYAH)
    this.olcumCizgiRengi = '#000000';
    this.olcumCizgiKalinligi = 1.5;
    this.olcumOffsetMesafe = 10;
    this.olcumYaziFontSize = 12;
    this.olcumYaziRengi = '#000000';
    this.olcumYaziBackgroundAlpha = 0;

    localStorage.removeItem('FikoThreeJSAyarlari');
    this.cd.detectChanges();
    this.kaydetAyarlar();
  }

  /**
   * Sekme aç/kapa
   */
  toggleSekme(sekme: string) {
    Object.keys(this.sekmeler).forEach(key => {
      (this.sekmeler as any)[key] = key === sekme ? !(this.sekmeler as any)[key] : false;
    });
  }

  /**
   * Dialogu kapat
   */
  kapat() {
    // Hem instance.kapa() hem de modal hide'ı çağır
    if (this.pencere?.instance) {
      this.pencere.instance.kapa();
    }
    // Her durumda modal hide'ı çağır (fallback)
    // Not: Bu component için modal ID dinamik olarak oluşturulmuyor, parent'tan gelen pencere kullanılıyor
  }
}
